import React, { useEffect, useState } from 'react';
import { FlattenedMenuItem } from '../../utils/menuFlattener';
import NavigationBar from './NavigationBar/NavigationBar';
import MobileSideNav from './MobileSideNav/MobileSideNav';
import SearchInputForm from './Search/SearchInputForm/SearchInputForm';
import MobileSideSearch from './Search/MobileSideSearch/MobileSideSearch';
import LoginNotification, { AlertContent } from './AlertBar/LoginNotification';
import AfterLoginModal, { AfterLoginModalContent } from './AlertBar/AfterLoginModal';
import { getCookie } from '../../utils/cookieManager';
import { buildLoginUrl } from '../../utils/authHelper';
import { HeaderMode } from '../../contexts/MinisiteLayoutContext';
import { useAuth } from '../../contexts/AuthProvider';
import { resolveUserDisplayName } from '../../utils/userIdentity';
import '../../css/now-icon.css';
import './Header.scss';

type HeaderProps = {
  mode?: HeaderMode;
};

type HeaderData = {
  currentLang?: 'en' | 'zh' | string;
  username?: string | null;
  nickname?: string | null;
  login_url?: string;
  menus?: {
    desktopPrimary?: FlattenedMenuItem[];
    mobilePrimary?: FlattenedMenuItem[];
    mobileSecondary?: FlattenedMenuItem[];
    watchDesktop?: FlattenedMenuItem[];
    watchMobile?: FlattenedMenuItem[];
    logo?: string;
  };
  cust_type?: string;
  fsa?: string;
  npid?: string;
  nowid?: string;
  fsaType?: string;
  hideHeader?: boolean;
  enableLoginOverlay?: boolean;
  alertContent?: Partial<AlertContent>;
  afterLoginModal?: Partial<AfterLoginModalContent>;
  socialLinks?: {
    facebook?: string;
    instagram?: string;
    youtube?: string;
  };
  authVerified?: boolean;
};

/**
 * Custom hook to get header data from AuthProvider userData
 * Uses pre-fetched menu data populated during auth bootstrap
 */
function useHeaderData(): HeaderData {
  const { userData, authVerified } = useAuth();
  const currentLang = (userData?.currentLang || 'en') as 'en' | 'zh';
  const rawLoginUrl = userData?.login_url || '';
  const shouldUseNowId = !rawLoginUrl || rawLoginUrl === '/login' || rawLoginUrl.includes('wp-login');
  const fallbackLoginUrl = buildLoginUrl(currentLang, window.location.href);

  return {
    currentLang,
    username: resolveUserDisplayName(
      {
        username: userData?.username,
        nickname: userData?.nickname,
        email: userData?.email,
        mobile: userData?.mobile,
        nowid: userData?.nowid
      }
    ) || null,
    nickname: userData?.nickname || null,
    login_url: shouldUseNowId ? fallbackLoginUrl : rawLoginUrl,
    menus: {
      desktopPrimary: userData?.menus?.desktopPrimary || [],
      mobilePrimary: userData?.menus?.mobilePrimary || [],
      mobileSecondary: userData?.menus?.mobileSecondary || [],
      watchDesktop: userData?.menus?.watchDesktop || [],
      watchMobile: userData?.menus?.watchMobile || [],
      logo: userData?.menus?.logo || '',
    },
    cust_type: userData?.cust_type,
    fsa: userData?.fsa || '',
    npid: userData?.npid || '',
    nowid: userData?.nowid || '',
    fsaType: userData?.fsaType || '',
    hideHeader: Boolean(userData?.hideHeader),
    enableLoginOverlay: Boolean(userData?.enableLoginOverlay),
    alertContent: userData?.alertContent || {},
    afterLoginModal: userData?.afterLoginModal || {},
    socialLinks: userData?.socialLinks || {},
    authVerified,
  };
}


export default function Header({ mode = 'general' }: HeaderProps): React.ReactElement {
  const data = useHeaderData();
  const isProfileStatusPending = !data.authVerified;
  const [isMobileNavActive, setIsMobileNavActive] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [previousCustType, setPreviousCustType] = useState<string | undefined>(data.cust_type);
  const [showLoginNotification, setShowLoginNotification] = useState(false);
  const [alertContent, setAlertContent] = useState<AlertContent | null>(null);
  const [showAfterLoginModal, setShowAfterLoginModal] = useState(false);
  const [afterLoginModalContent, setAfterLoginModalContent] = useState<AfterLoginModalContent | null>(null);

  // Global GA code injection
  useEffect(() => {
    const fetchGAData = async () => {
      // Use site domain for fetching GA code from specific location
      const domain = window.location.origin;
      const gaFileUrl = domain + '/wp-content/uploads/GA/GA-code-global.html';
      
      try {
        const response = await fetch(gaFileUrl);
        if (!response.ok) return;

        const htmlText = await response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(htmlText, 'text/html');

        // Check if the doc.body or doc.head contains the app root, if so, abort to avoid loop/duplicates
        if (doc.getElementById('root') || doc.querySelector('#root')) {
          console.warn('GA fetch returned SPA index.html instead of GA code, aborting injection.');
          return;
        }

        // Cleanup any previously injected elements to avoid duplicates on re-mount
        document.querySelectorAll('[data-ga-injected]').forEach(el => el.remove());
        // Also cleanup comments - since we can't use querySelector for comments, we skip for now 
        // as useEffect with [] dependency should only run once.

        const injectContent = (sourceContainer: HTMLElement, targetContainer: HTMLElement, prepend = false) => {
          // If prepending, keep track of the original first child to insert everything before it in source order
          const initialFirstChild = targetContainer.firstChild;
          let currentReference = initialFirstChild;
          
          // Process nodes in exact order as they appear in the source HTML
          Array.from(sourceContainer.childNodes).forEach(oldNode => {
            if (oldNode.nodeType === Node.ELEMENT_NODE) {
              const element = oldNode as HTMLElement;
              const tagName = element.tagName.toUpperCase();
              
              // Safeguard: Never inject a second root div or the app itself
              if (element.id === 'root' || element.querySelector('#root')) {
                return;
              }

              // Only inject tags typically used for tracking and metadata
              if (['SCRIPT', 'NOSCRIPT', 'STYLE', 'META', 'LINK'].includes(tagName)) {
                if (tagName === 'SCRIPT') {
                  const oldScript = element as HTMLScriptElement;
                  const newScript = document.createElement('script');
                  
                  Array.from(oldScript.attributes).forEach(attr => {
                    newScript.setAttribute(attr.name, attr.value);
                  });
                  newScript.setAttribute('data-ga-injected', 'true');
                  
                  const targetSrc = oldScript.src || oldScript.getAttribute('src');
                  if (targetSrc) {
                    newScript.setAttribute('data-ga-src', targetSrc);
                    newScript.src = targetSrc;
                  } else {
                    newScript.textContent = oldScript.textContent;
                  }
                  
                  if (prepend) {
                    targetContainer.insertBefore(newScript, currentReference);
                  } else {
                    targetContainer.appendChild(newScript);
                  }
                } else {
                  const clonedNode = element.cloneNode(true) as HTMLElement;
                  clonedNode.setAttribute('data-ga-injected', 'true');
                  if (prepend) {
                    targetContainer.insertBefore(clonedNode, currentReference);
                  } else {
                    targetContainer.appendChild(clonedNode);
                  }
                }
              }
            } else if (oldNode.nodeType === Node.COMMENT_NODE) {
              // Inject all comments exactly as they are in the source
              if (prepend) {
                targetContainer.insertBefore(oldNode.cloneNode(true), currentReference);
              } else {
                targetContainer.appendChild(oldNode.cloneNode(true));
              }
            } else if (oldNode.nodeType === Node.TEXT_NODE) {
              if (oldNode.textContent?.trim()) {
                if (prepend) {
                  targetContainer.insertBefore(oldNode.cloneNode(true), currentReference);
                } else {
                  targetContainer.appendChild(oldNode.cloneNode(true));
                }
              }
            }
          });
        };

        // Inject <head> content - to the bottom of document head (prepend=false)
        if (doc.head) {
          injectContent(doc.head, document.head, false);
        }

        // Inject <body> content - to the top of document body (prepend=true)
        if (doc.body) {
          injectContent(doc.body, document.body, true);
        }
      } catch (error) {
        console.error('Error fetching GA code:', error);
      }
    };

    fetchGAData();
  }, []);

  // Manage body scroll lock when modals are open
  useEffect(() => {
    if (isMobileNavActive || isSearchOpen) {
      document.body.classList.add('modal-open');
    } else {
      document.body.classList.remove('modal-open');
    }
    
    // Cleanup on unmount
    return () => {
      document.body.classList.remove('modal-open');
    };
  }, [isMobileNavActive, isSearchOpen]);

  // Detect login changes and show notification/modal
  useEffect(() => {
    // Check if user logged in (cust_type changed from not-logged-in to something else)
    const wasNotLoggedIn = previousCustType === 'not-logged-in' || previousCustType === undefined;
    const isNowLoggedIn = data.cust_type && data.cust_type !== 'not-logged-in';
    const hasHiddenNotification = getCookie('hide_login_notice');
    const hasHiddenModal = getCookie('do_not_show_again');

    // Show notification if user just logged in and hasn't hidden it
    if (wasNotLoggedIn && isNowLoggedIn && !hasHiddenNotification) {
      const wpAlertContent = (data.alertContent || {}) as Partial<AlertContent>;
      
      setAlertContent({
        heading: wpAlertContent.heading || 'Welcome back!',
        message: wpAlertContent.message || 'Thank you for logging in to Now TV.',
        ctaLabel: wpAlertContent.ctaLabel,
        ctaUrl: wpAlertContent.ctaUrl,
        showCTA: wpAlertContent.showCTA || false
      });
      setShowLoginNotification(true);
    }

    // Show after login modal if enabled and not dismissed
    if (wasNotLoggedIn && isNowLoggedIn && !hasHiddenModal) {
      const wpModalContent = (data.afterLoginModal || {}) as Partial<AfterLoginModalContent>;
      const enableLoginOverlay = data.enableLoginOverlay || false;
      
      if (enableLoginOverlay) {
        setAfterLoginModalContent({
          title: wpModalContent.title,
          content: wpModalContent.content || '<h3>Welcome!</h3><p>Thank you for logging in.</p>',
          buttonLabel: wpModalContent.buttonLabel || 'OK',
          doNotShowLabel: wpModalContent.doNotShowLabel || 'Do not show this message again'
        });
        setShowAfterLoginModal(true);
      }
    }

    // Update previous cust_type
    setPreviousCustType(data.cust_type);
  }, [data.cust_type, previousCustType]);

  // Debug: Log the header data to browser console
  useEffect(() => {
    console.log('Header data:', data);
    console.log('Menus:', data.menus);
    console.log('Customer Type:', data.cust_type);
    console.log('Desktop Primary Menu Items:', data.menus?.desktopPrimary);
    if (data.menus?.desktopPrimary && data.menus.desktopPrimary.length > 0) {
      console.log('First menu item displayFor:', data.menus.desktopPrimary[0].displayFor);
    }
    console.log('Username:', data.username);
    console.log('Nickname:', data.nickname);
    console.log('Current language:', data.currentLang);
  }, [data]);

  const handleBurgerMenuClick = () => {
    setIsMobileNavActive(true);
  };

  const handleCloseMobileNav = () => {
    setIsMobileNavActive(false);
  };

  const handleSearchClick = () => {
    setIsSearchOpen(!isSearchOpen);
  };
  
  const handleLanguageChange = (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      
      const lang = e.currentTarget.getAttribute('data-value') || '';
      const main_link = window.location.href.split('?');
      let main_url;
      
      if (main_link.length >= 2) {
        const prefix = encodeURIComponent('lang') + '=';
        const pars = main_link[1].split(/[&;]/g);
        
        // Reverse iteration as may be destructive
        for (let i = pars.length; i-- > 0;) {
          // Idiom for string.startsWith
          if (pars[i].lastIndexOf(prefix, 0) !== -1) {
            pars.splice(i, 1);
          }
        }
        main_url = main_link[0] + '?' + pars.join('&');
      } else {
        main_url = window.location.href;
      }
      
      // Set cookie with 20 years expiry
      const d = new Date();
      d.setTime(d.getTime() + (20 * 365 * 24 * 60 * 60 * 1000));
      const expires = "expires=" + d.toUTCString();
      document.cookie = "lang=" + lang + ";" + expires + ";SameSite=None;Secure;domain=.nowtv.now.com;path=/";
      
      window.history.replaceState(null, '', main_url);
      window.location.reload();
    };

  // Build login URL
  const loginUrl = data.login_url || 
    `https://id.now.com/nowidform/login.form?lang=${data.currentLang}&cancel=${window.location.origin}&redirect=${window.location.origin}/api/validate-login.php?redirect_to=${window.location.href}`;

  // Check if header should be hidden
  const hideHeader = data.hideHeader || false;
  
  // Don't render header if hidden
  if (hideHeader) {
    return <></>;
  }

  return (
    <>
      <input name='lang' value={data.currentLang === 'zh' ? 'mcHbFvVz6GasaoYXRD6O4Q==' : 'op/UwDjU7jkgMlWyDIDgUg=='} type='hidden' />
      <input type='hidden' value={data.fsa} name='fas'/>
      <input type='hidden' value={data.npid} name='npid'/>
      <input type='hidden' value={data.cust_type === 'ott' ? 'true' : 'false'} name='is_user_ott'/>
      <input type='hidden' value={data.nowid} name='nowid'/>
      <input type='hidden' value={data.fsaType} name='fsaType'/>
      <input type='hidden' value={data.cust_type} name='cust_type'/>
      
      {mode !== 'hidden' && (
        <>
          <NavigationBar
            mode={mode}
            navItems={data.menus?.desktopPrimary}
            watchNavItems={data.menus?.watchDesktop}
            cust_type={data.cust_type}
            username={data.username || undefined}
            currentLang={data.currentLang as 'en' | 'zh'}
            onBurgerMenuClick={handleBurgerMenuClick}
            isSearchOpen={isSearchOpen}
            onSearchClick={handleSearchClick}
            isMobileMenuOpen={isMobileNavActive}
            loginUrl={loginUrl}
            isProfileLoading={isProfileStatusPending}
            logoUrl={data.menus?.logo}
          />

          {/* After Login Modal */}
          {showAfterLoginModal && afterLoginModalContent && (
            <AfterLoginModal
              content={afterLoginModalContent}
              onClose={() => setShowAfterLoginModal(false)}
              autoShow={true}
            />
          )}
          
          {/* Desktop Search Overlay - Only show on desktop (>= 992px) */}
          {isSearchOpen && window.innerWidth >= 992 && (
            <>
              <div 
                className="search-overlay" 
                onClick={() => setIsSearchOpen(false)}
              />
              <SearchInputForm isOpen={isSearchOpen} />
            </>
          )}

          {/* Mobile Side Search Panel - Only show on mobile (< 992px) */}
          {isSearchOpen && window.innerWidth < 992 && (
            <>
              <div 
                className="search-overlay" 
                onClick={() => setIsSearchOpen(false)}
              />
              <div className={`mobile-search-container ${!isSearchOpen ? 'closed' : ''}`}>
                <MobileSideSearch onClose={() => setIsSearchOpen(false)} />
              </div>
            </>
          )}
          
          <div className={`mobile-nav-wrapper ${isMobileNavActive ? 'active' : ''}`}>
            {mode !== 'logo-language' && (
              <MobileSideNav
                mode={mode}
                isActive={isMobileNavActive}
                onClose={handleCloseMobileNav}
                navItems={data.menus?.mobilePrimary}
                secondaryNavItems={data.menus?.mobileSecondary}
                watchMenuItems={data.menus?.watchMobile}
                cust_type={data.cust_type}
                username={data.username || undefined}
                currentLang={data.currentLang as 'en' | 'zh'}
                loginUrl={loginUrl}
                onLanguageChange={handleLanguageChange}
                socialLinks={{
                  facebook: data.socialLinks?.facebook || 'https://www.facebook.com/NowTV.hongkong/',
                  instagram: data.socialLinks?.instagram || 'https://www.instagram.com/nowtv.hk',
                  youtube: data.socialLinks?.youtube || 'https://www.youtube.com/user/nowtv'
                }}
                isProfileLoading={isProfileStatusPending}
              />
            )}
          </div>
          
          {isMobileNavActive && (
            <div className="mobile-nav-overlay" onClick={handleCloseMobileNav}></div>
          )}
        </>
      )}
    </>
  );
}
